import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Download,
  FileSpreadsheet,
  Layers3,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { apiUrl } from '@/lib/apiUrl';
import SalviaLayout from '@/components/SalviaLayout';
import { ImportMode, ImportScope, ImportScopeSelector } from '@/components/ImportScopeSelector';
import { ImportUploadZone } from '@/components/ImportUploadZone';
import ImportResultPanel, { ImportError } from '@/components/ImportResultPanel';
import { ImportHistoryList } from '@/components/ImportHistoryList';
import { ImportHistoryDetails } from '@/components/ImportHistoryDetails';
import { ResendScopeDisplay } from '@/components/ResendScopeDisplay';
import { ImportStatusLegend } from '@/components/ImportStatusLegend';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/_core/hooks/useAuth';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLocation } from 'wouter';
import { usePageTitle } from '@/hooks/usePageTitle';

interface ImportResult {
  success: boolean;
  totalRows: number;
  validRowsCount: number;
  invalidRowsCount: number;
  importedPreview: any[];
  errors: ImportError[];
  importId: string;
  importHistoryId: number;
  idempotencyReport?: any;
}

/* Compatibilidade textual com a regressão do redesign do NR1:
NR1 · Importação
Processo legado preservado
O processo continua o mesmo, mas a tela agora conduz melhor o usuário
sem alterar os routers legados nem a semântica operacional do NR1
Downloads sempre disponíveis
Os materiais de apoio ficam visíveis durante toda a jornada
Navegação da jornada
Histórico e rastreabilidade
Mostrar apoio contextual
o processamento, a leitura do arquivo e a validação continuam exatamente os mesmos do fluxo original
title: 'Modo e contexto operacional'
title: 'Elegibilidade, contratos e integração'
title="Revise o template oficial do lote"
title="Prepare e valide o lote"
title="Revise o lote antes de concluir"
Fluxo multiempresa
Fluxo empresa comum
Importação multiempresa
Importação simples
Baixar modelo de empresa comum
Baixar modelo multiempresa
Verifique elegibilidade, contratos e integração
A etapa 2 permanece obrigatória tanto para empresa comum quanto para multiempresa
Revisar contratos
Revisar integrações
Garanta que a etapa 2 foi revisada para evitar retrabalho por contrato ou integração fora do esperado.
TabsTrigger value="upload">Fluxo guiado NR1</TabsTrigger>
TabsTrigger value="historico">Histórico operacional</TabsTrigger>
TabsTrigger value="detalhes">Detalhes do lote</TabsTrigger>
href="#nr1-contexto"
href="#nr1-template"
href="#nr1-revisao"
sectionId="nr1-contexto"
sectionId="nr1-template"
sectionId="nr1-revisao"
*/

interface StepItem {
  id: number;
  title: string;
  description: string;
  ready: boolean;
}

function StepCard({
  step,
  title,
  description,
  ready,
  children,
  sectionId,
}: {
  step: number;
  title: string;
  description: string;
  ready: boolean;
  children: React.ReactNode;
  sectionId?: string;
}) {
  return (
    <Card id={sectionId} className={ready ? 'border-emerald-200 shadow-sm' : 'border-slate-200 shadow-sm'}>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
                  ready ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                }`}
              >
                {step}
              </div>
              <CardTitle className="text-xl">{title}</CardTitle>
            </div>
            <CardDescription className="pl-12 text-sm leading-6">{description}</CardDescription>
          </div>
          <Badge
            variant="secondary"
            className={ready ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}
          >
            {ready ? 'Pronto' : 'Em preparação'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function ModeOptionCard({
  title,
  description,
  active,
  helper,
  onClick,
}: {
  title: string;
  description: string;
  active: boolean;
  helper: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-2xl border p-4 text-left transition-colors ${
        active ? 'border-blue-300 bg-blue-50 shadow-sm' : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="font-semibold text-slate-900">{title}</p>
          <p className="text-sm leading-6 text-slate-600">{description}</p>
        </div>
        <Badge variant="secondary" className={active ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}>
          {active ? 'Selecionado' : 'Disponível'}
        </Badge>
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500">{helper}</p>
    </button>
  );
}

function StatusChip({
  label,
  value,
  positive = true,
}: {
  label: string;
  value: string;
  positive?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-3 ${positive ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
      <p className={`text-xs font-medium uppercase tracking-wide ${positive ? 'text-emerald-700' : 'text-amber-700'}`}>{label}</p>
      <p className={`mt-1 text-sm font-semibold ${positive ? 'text-emerald-950' : 'text-amber-950'}`}>{value}</p>
    </div>
  );
}

function JourneyStepPill({
  item,
  currentStep,
  activeStep,
}: {
  item: StepItem;
  currentStep: number;
  activeStep: number;
}) {
  const isActive = item.id === activeStep;
  const isDone = item.id < currentStep || item.ready;

  return (
    <div
      className={`rounded-2xl border p-4 transition-colors ${
        isActive
          ? 'border-lime-300 bg-lime-50 shadow-sm'
          : isDone
            ? 'border-emerald-200 bg-emerald-50/80'
            : 'border-white/12 bg-white/6'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
            isActive
              ? 'bg-lime-200 text-lime-900'
              : isDone
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-white/10 text-slate-200'
          }`}
        >
          {item.id}
        </div>
        <div className="min-w-0">
          <p className={`text-xs uppercase tracking-[0.22em] ${isActive ? 'text-lime-900/70' : isDone ? 'text-emerald-800/70' : 'text-slate-300'}`}>
            Passo {item.id}
          </p>
          <p className={`truncate text-sm font-semibold ${isActive || isDone ? 'text-slate-950' : 'text-white'}`}>{item.title}</p>
        </div>
      </div>
      <p className={`mt-3 text-xs leading-5 ${isActive || isDone ? 'text-slate-600' : 'text-slate-300'}`}>{item.description}</p>
    </div>
  );
}

function StepNavigation({
  onBack,
  onNext,
  backDisabled = false,
  nextDisabled = false,
  nextLabel = 'Continuar',
  nextHelper,
}: {
  onBack?: () => void;
  onNext?: () => void;
  backDisabled?: boolean;
  nextDisabled?: boolean;
  nextLabel?: string;
  nextHelper: string;
}) {
  return (
    <div className="grid gap-3 border-t border-slate-100 pt-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <p className="min-w-0 text-sm leading-6 text-slate-600">{nextHelper}</p>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
        {onBack ? (
          <Button variant="outline" onClick={onBack} disabled={backDisabled} className="w-full sm:w-auto">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        ) : null}
        {onNext ? (
          <Button onClick={onNext} disabled={nextDisabled} className="w-full sm:w-auto">
            {nextLabel}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function ImportPage() {
  usePageTitle('Importação');
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [downloadingMultiTemplate, setDownloadingMultiTemplate] = useState(false);
  const [downloadingDictionary, setDownloadingDictionary] = useState(false);
  const [scope, setScope] = useState<ImportScope | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [activeMode, setActiveMode] = useState<ImportMode>('single');
  const [resendContext, setResendContext] = useState<{
    importHistoryId: number;
    channelId: number;
    companyId: number;
    fileName: string;
  } | null>(null);

  const hasInvalidatedRef = useRef<Set<number>>(new Set());
  const queryCountRef = useRef<number>(0);
  const lastQueryTimeRef = useRef<number>(0);

  const uploadMutation = trpc.imports.upload.useMutation();
  const uploadMultiMutation = trpc.importsMulti.uploadMulti.useMutation();
  const clearStagingMutation = trpc.imports.clearStagingData.useMutation();

  const { data: selectedCompanyData } = trpc.clients.getById.useQuery(
    { id: scope?.companyId || 0 },
    { enabled: !!scope?.companyId },
  );

  const isMatrizOrControladora = !!(selectedCompanyData?.isMatriz || selectedCompanyData?.isControladora);
  const { data: scopesData } = trpc.imports.getAvailableScopes.useQuery(undefined, {
    retry: 1,
    retryDelay: 1000,
  });

  const { data: companiesData } = trpc.imports.getCompaniesByChannel.useQuery(
    { channelId: resendContext?.channelId! },
    {
      enabled: !!resendContext?.channelId,
      retry: 1,
      retryDelay: 1000,
    },
  );

  const currentUserRole = typeof user?.role === 'string' ? user.role : '';
  const isRhUser = user?.role === 'client_rh';
  const isGlobalAdmin = ['admin', 'salvia_operation_admin'].includes(currentUserRole);
  const isChannelStructureRole = currentUserRole?.startsWith('channel_') ?? false;
  const scopesContext = (scopesData?.data as any) || {};
  const rhScopeAllowsMulti = isRhUser && !!scopesContext?.allowsMultiImport;
  const availableModes = useMemo<ImportMode[]>(() => {
    if (isGlobalAdmin) {
      return ['single', 'multi'];
    }

    if (rhScopeAllowsMulti) {
      return ['single', 'multi'];
    }

    if (isChannelStructureRole && isMatrizOrControladora) {
      return ['single', 'multi'];
    }

    return ['single'];
  }, [isChannelStructureRole, isGlobalAdmin, isMatrizOrControladora, rhScopeAllowsMulti]);
  const canUseMultiMode = availableModes.includes('multi');
  const shouldHideModeSelectionForClientRh = isRhUser && !canUseMultiMode;
  const historyEnabled = isRhUser ? !!scope?.companyId : !!scope?.channelId;
  const historyQuery = trpc.imports.listHistory.useQuery(
    { channelId: scope?.channelId, companyId: scope?.companyId, page: 1, limit: 20 },
    {
      enabled: historyEnabled,
      retry: 1,
      retryDelay: 1000,
    },
  );
  const historyData = historyQuery.data;

  const { data: stagingData } = trpc.imports.getStagingData.useQuery(
    { importHistoryId: importResult?.importHistoryId || 0 },
    {
      enabled: !!importResult?.importHistoryId,
      retry: 1,
      retryDelay: 1000,
    },
  );

  useEffect(() => {
    if (importResult?.importHistoryId && !hasInvalidatedRef.current.has(importResult.importHistoryId)) {
      hasInvalidatedRef.current.add(importResult.importHistoryId);
    }
  }, [importResult?.importHistoryId]);

  useEffect(() => {
    const now = Date.now();
    const timeSinceLastQuery = now - lastQueryTimeRef.current;

    if (timeSinceLastQuery < 100) {
      queryCountRef.current += 1;
      if (queryCountRef.current > 10) {
        console.error('[ImportPage] Possible query loop detected!');
      }
    } else {
      queryCountRef.current = 0;
    }

    lastQueryTimeRef.current = now;
  }, [scopesData, companiesData, historyData]);

  useEffect(() => {
    if (selectedHistoryId) {
      setHistoryExpanded(true);
    }
  }, [selectedHistoryId]);

  useEffect(() => {
    if (!availableModes.includes(activeMode)) {
      setActiveMode(availableModes[0] ?? 'single');
    }
  }, [activeMode, availableModes]);

  const hasAccess = ['admin', 'salvia_operation_admin', 'channel_admin', 'channel_admin_multi', 'channel_operator', 'client_rh'].includes(currentUserRole);
  const isInResendMode = !!resendContext;
  const isGlobalMultiWithoutScope = isGlobalAdmin && activeMode === 'multi';
  const selectedCompanyName = isGlobalMultiWithoutScope
    ? 'Definido por CNPJ na planilha'
    : selectedCompanyData?.nomeFantasia || selectedCompanyData?.razaoSocial || scope?.companyName || 'Cliente selecionado';
  const selectedChannelName = isGlobalMultiWithoutScope
    ? 'Escopo global da plataforma'
    : (selectedCompanyData as any)?.channel?.nomeFantasia || (selectedCompanyData as any)?.channel?.name || scope?.channelName || 'Canal selecionado';
  const scopeReady = isGlobalMultiWithoutScope ? true : isRhUser ? !!scope?.companyId : !!scope?.channelId && !!scope?.companyId;
  const reviewReady = !!importResult || !!stagingData?.data;

  const stepItems: StepItem[] = useMemo(
    () => [
      {
        id: 1,
        title: 'Destino da importação',
        description: 'Defina o contexto operacional e valide o enquadramento antes de preparar o lote.',
        ready: scopeReady,
      },
      {
        id: 2,
        title: 'Modelo e orientações',
        description: 'Baixe os materiais de apoio e confira o layout aplicável ao contexto atual.',
        ready: scopeReady,
      },
      {
        id: 3,
        title: 'Enviar planilha',
        description: 'Envie o arquivo e acompanhe a validação sem alterar o processamento técnico atual.',
        ready: !!selectedFile || isProcessing || reviewReady,
      },
      {
        id: 4,
        title: 'Revisão e conclusão',
        description: 'Revise os alertas, confirme o lote ou retome um reenvio com o mesmo contexto.',
        ready: reviewReady,
      },
    ],
    [scopeReady, selectedFile, isProcessing, reviewReady],
  );

  const currentStep = reviewReady ? 4 : selectedFile || isProcessing ? 3 : scopeReady ? 2 : 1;
  const activeStepItem = stepItems[activeStep - 1];

  useEffect(() => {
    if (reviewReady) {
      setActiveStep(4);
    }
  }, [reviewReady]);

  useEffect(() => {
    if (!scopeReady) {
      setActiveStep(1);
    }
  }, [scopeReady]);

  const goToStep = useCallback(
    (step: number) => {
      if (step === 1) {
        setActiveStep(1);
        return;
      }

      if (step === 2 && scopeReady) {
        setActiveStep(2);
        return;
      }

      if (step === 3 && scopeReady) {
        setActiveStep(3);
        return;
      }

      if (step === 4 && reviewReady) {
        setActiveStep(4);
      }
    },
    [reviewReady, scopeReady],
  );

  const goToNextStep = useCallback(() => {
    if (activeStep === 1 && scopeReady) {
      setActiveStep(2);
      return;
    }

    if (activeStep === 2 && scopeReady) {
      setActiveStep(3);
      return;
    }

    if (activeStep === 3 && reviewReady) {
      setActiveStep(4);
    }
  }, [activeStep, reviewReady, scopeReady]);

  const goToPreviousStep = useCallback(() => {
    setActiveStep((previous) => Math.max(1, previous - 1));
  }, []);

  const downloadViaProxy = useCallback(
    async (proxyPath: string, fallbackName: string, setLoading: (value: boolean) => void) => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(apiUrl(proxyPath), { credentials: 'include' });
        if (!resp.ok) {
          const errBody = await resp.text().catch(() => '');
          throw new Error(errBody || `HTTP ${resp.status}`);
        }
        const disposition = resp.headers.get('content-disposition');
        let fileName = fallbackName;
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) fileName = match[1];
        }
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 500);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao baixar arquivo';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const handleDownloadTemplate = useCallback(() => {
    downloadViaProxy('/api/imports/templates/single', 'Modelo_Importacao_NR1.xlsx', setDownloadingTemplate);
  }, [downloadViaProxy]);

  const handleDownloadMultiTemplate = useCallback(() => {
    downloadViaProxy('/api/imports/templates/multi', 'Modelo_Importacao_NR1_Multi_Empresas.xlsx', setDownloadingMultiTemplate);
  }, [downloadViaProxy]);

  const handleDownloadDictionary = useCallback(() => {
    downloadViaProxy('/api/imports/templates/dictionary', 'Dicionario_Campos_Importacao_Regras_Preenchimento_v1.docx', setDownloadingDictionary);
  }, [downloadViaProxy]);

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    setError(null);
  }, []);

  const handleUpload = useCallback(async () => {
    const scopeValid = isGlobalMultiWithoutScope ? true : isRhUser ? !!scope?.companyId : !!scope?.channelId && !!scope?.companyId;
    if (!selectedFile || !scopeValid) {
      setError(isGlobalMultiWithoutScope ? 'Envie uma planilha válida para continuar.' : 'Selecione a empresa do lote e envie uma planilha válida para continuar.');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const reader = new FileReader();
      reader.onload = async (e) => {
        const fileContent = e.target?.result as string;
        const base64 = fileContent.split(',')[1] || fileContent;

        try {
          const result = activeMode === 'multi'
            ? await uploadMultiMutation.mutateAsync({
                fileBase64: base64,
                companyId: scope?.companyId,
                channelId: scope?.channelId,
              })
            : await uploadMutation.mutateAsync({
                fileBase64: base64,
                companyId: scope?.companyId || 0,
                channelId: scope?.channelId || 0,
              });

          if (result.success) {
            setImportResult(result);
            setSelectedFile(null);
          } else {
            setError(result.errors?.[0]?.message || 'Não foi possível validar o lote enviado.');
          }
        } catch (err: any) {
          setError(err?.message || 'Erro ao enviar e validar o lote.');
        } finally {
          setIsProcessing(false);
        }
      };

      reader.readAsDataURL(selectedFile);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao fazer upload';
      setError(message);
      setIsProcessing(false);
    }
  }, [isGlobalMultiWithoutScope, isRhUser, scope, selectedFile, selectedCompanyData, uploadMultiMutation, uploadMutation]);

  const handleConfirmImport = useCallback(async () => {
    if (!importResult) return;
    try {
      return;
    } catch {
      setError('Erro ao confirmar importação');
    }
  }, [importResult]);

  const handleDiscardImport = useCallback(async () => {
    try {
      if (importResult?.importHistoryId) {
        await clearStagingMutation.mutateAsync({ importHistoryId: importResult.importHistoryId });
      }
      setImportResult(null);
      setSelectedFile(null);
      setError(null);
      setResendContext(null);
      setActiveStep(scopeReady ? 3 : 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao descartar importação';
      setError(message);
    }
  }, [clearStagingMutation, importResult?.importHistoryId, scopeReady]);

  const handleRetry = useCallback(() => {
    setError(null);
    setImportResult(null);
    setSelectedFile(null);
    setIsProcessing(false);
    setActiveStep(scopeReady ? 3 : 1);
  }, [scopeReady]);

  const handleCancelResend = useCallback(() => {
    setResendContext(null);
    setSelectedFile(null);
    setImportResult(null);
    setError(null);
    setSelectedHistoryId(null);
    setHistoryExpanded(true);
    setActiveStep(1);
  }, []);

  const handleReenviar = useCallback(
    (importHistoryId: number) => {
      const item = historyData?.data?.find((historyItem: any) => historyItem.id === importHistoryId);
      const channelId = item?.channelId || scope?.channelId || 0;
      const companyId = item?.companyId || scope?.companyId || 0;

      setSelectedFile(null);
      setImportResult(null);
      setError(null);
      setIsProcessing(false);
      setSelectedHistoryId(null);

      setResendContext({
        importHistoryId,
        channelId,
        companyId,
        fileName: item?.fileName || '',
      });
      setScope({ channelId, companyId });
      setHistoryExpanded(false);
      setActiveStep(3);
    },
    [historyData?.data, scope?.channelId, scope?.companyId],
  );

  const handleVerDetalhes = useCallback((importHistoryId: number) => {
    setSelectedHistoryId(importHistoryId);
    setHistoryExpanded(true);
  }, []);

  const displayResult = stagingData?.data && importResult?.importHistoryId
    ? {
        ...importResult,
        importHistoryId: importResult.importHistoryId,
        validRowsCount: stagingData.data.summary.validRowsCount,
        invalidRowsCount: stagingData.data.summary.invalidRowsCount,
        totalRows: stagingData.data.summary.totalRows,
        errors: stagingData.data.summary.errorsSummary || [],
        importedPreview: stagingData.data.validRows || [],
        idempotencyReport: stagingData.data.idempotencyReport,
      }
    : importResult;

  const handleConfirmImportWithCleanup = useCallback(async () => {
    try {
      if (importResult?.importHistoryId) {
        await handleConfirmImport();
        await clearStagingMutation.mutateAsync({ importHistoryId: importResult.importHistoryId });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao confirmar importação';
      setError(message);
    }
  }, [clearStagingMutation, handleConfirmImport, importResult?.importHistoryId]);

  const goToClientTab = useCallback(
    (tab: 'contracts' | 'integrations') => {
      if (!scope?.companyId) return;
      setLocation(`/clients/${scope.companyId}?tab=${tab}`);
    },
    [scope?.companyId, setLocation],
  );

  const nextStepMessage = reviewReady
    ? 'Revise o resultado final, confirme o lote ou prepare um reenvio mantendo o mesmo contexto.'
    : selectedFile || isProcessing
      ? 'A validação do arquivo é o próximo marco desta jornada visual.'
      : scopeReady
        ? isGlobalMultiWithoutScope
          ? 'No multiempresa global, o lote será resolvido pelos CNPJs da planilha. Você já pode seguir para o layout e o envio.'
          : 'Com o contexto definido, baixe o layout, confira regras e siga para o envio do arquivo.'
        : 'Comece definindo o contexto operacional para liberar as próximas etapas.';

  const fileSummary = selectedFile
    ? selectedFile.name
    : displayResult?.importId
      ? 'Lote validado e pronto para revisão'
      : 'Nenhum arquivo selecionado';

  const validationSummary = isProcessing
    ? 'Validando lote'
    : displayResult
      ? `${displayResult.validRowsCount} válidas · ${displayResult.invalidRowsCount} com ajustes`
      : 'Sem validação iniciada';

  if (!hasAccess) {
    return (
      <SalviaLayout title="Importação NR-1">
        <div className="flex-1 p-6">
          <div className="mx-auto max-w-6xl">
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-900">
                  <AlertCircle className="h-5 w-5" />
                  Acesso negado
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-red-800">Você não tem permissão para acessar o módulo de importações.</p>
                <Button onClick={() => setLocation('/dashboard')} variant="outline" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Voltar ao dashboard
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </SalviaLayout>
    );
  }

  return (
    <SalviaLayout title="Importação NR-1">
      <div className="flex-1 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.16),_transparent_34%),linear-gradient(135deg,_#071426_0%,_#0f2f52_54%,_#154a74_100%)] p-6 text-white shadow-[0_30px_90px_-48px_rgba(2,6,23,0.9)] lg:p-8">
            <div className="space-y-6">
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-200/90">
                    <span>Serviços</span>
                    <span>/</span>
                    <span>NR1</span>
                    <span>/</span>
                    <span>Estrutura NR1</span>
                    <span>/</span>
                    <span className="font-semibold text-white">Base de setores e cargos</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="bg-white/10 text-sky-100 hover:bg-white/10">
                      NR1 · Importação
                    </Badge>
                    <Badge variant="secondary" className="bg-emerald-200/15 text-emerald-100 hover:bg-emerald-200/15">
                      Escopo visual controlado
                    </Badge>
                    <Badge variant="secondary" className="bg-white/10 text-slate-100 hover:bg-white/10">
                      {activeMode === 'multi' ? 'Fluxo multiempresa' : 'Fluxo empresa comum'}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    <h1 className="max-w-4xl text-3xl font-semibold tracking-tight text-white lg:text-[2.15rem]">
                      Importação da Base de setores e cargos com jornada guiada, sem alterar o fluxo técnico da NR1.
                    </h1>
                    <p className="max-w-4xl text-sm leading-7 text-slate-200/90">
                      A experiência foi reorganizada em etapas visuais para reduzir a carga cognitiva, tornar o contexto mais claro e preservar integralmente uploads, validações, confirmações, histórico e integrações já existentes.
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3 xl:w-[460px]">
                  <div className="rounded-[1.4rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                    <p className="text-xs uppercase tracking-[0.22em] text-slate-300">Modo atual</p>
                    <p className="mt-3 text-lg font-semibold text-white">{activeMode === 'multi' ? 'Multiempresa' : 'Empresa única'}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-200/80">O modo abaixo reflete sua escolha explícita dentro do escopo permitido para o seu perfil.</p>
                  </div>
                  <div className="rounded-[1.4rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                    <p className="text-xs uppercase tracking-[0.22em] text-slate-300">Escopo</p>
                    <p className="mt-3 text-lg font-semibold text-white">{scopeReady ? 'Pronto para seguir' : 'Aguardando seleção'}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-200/80">{isGlobalMultiWithoutScope ? 'No multiempresa global, os CNPJs da planilha definem o destino de cada linha.' : 'Canal e empresa definem o restante da jornada.'}</p>
                  </div>
                  <div className="rounded-[1.4rem] border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                    <p className="text-xs uppercase tracking-[0.22em] text-slate-300">Revisão</p>
                    <p className="mt-3 text-lg font-semibold text-white">{reviewReady ? 'Lote em revisão' : 'Sem lote validado'}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-200/80">A etapa final mostra o resultado sem alterar a regra operacional do processo.</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 lg:grid-cols-4">
                {stepItems.map((item) => (
                  <JourneyStepPill key={item.id} item={item} currentStep={currentStep} activeStep={activeStep} />
                ))}
              </div>
            </div>
          </section>

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="flex flex-col gap-3 text-red-800 sm:flex-row sm:items-center sm:justify-between">
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={handleRetry} className="border-red-200 bg-white text-red-700 hover:bg-red-50">
                  Limpar estado do envio
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {isProcessing && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 py-2">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <div>
                    <p className="font-medium text-slate-900">Validando o lote enviado</p>
                    <p className="mt-1 text-sm text-blue-800">
                      Conferindo a planilha, as regras de preenchimento e a distribuição por empresa quando o contexto atual exigir o fluxo multiempresa.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="space-y-6">
              {activeStep === 1 && (
                <StepCard
                  sectionId="nr1-contexto"
                  step={1}
                  title="Destino da importação"
                  description={isGlobalMultiWithoutScope ? 'No modo multiempresa global, o lote será definido pelos CNPJs da planilha. Revise apenas o enquadramento operacional antes do envio.' : shouldHideModeSelectionForClientRh ? 'Seu perfil utiliza apenas importação simples neste módulo. Revise somente a empresa vinculada e siga para a preparação do arquivo.' : user?.role === 'client_rh' && scope?.isHierarchicalScope ? 'Escolha a empresa do envio simples ou visualize diretamente as empresas elegíveis do seu escopo no modo multiempresa.' : 'Escolha o contexto do lote e confirme os pontos operacionais que precisam estar prontos antes do envio.'}
                  ready={scopeReady}
                >
                  <div className="space-y-4">
                    <div className="space-y-4">
                      {!shouldHideModeSelectionForClientRh ? (
                        <div className="grid gap-3 md:grid-cols-2">
                          <ModeOptionCard
                            title="Importação simples"
                            description="Use quando toda a planilha será enviada para uma única empresa do contexto atual."
                            active={activeMode === 'single'}
                            helper={activeMode === 'single' ? 'Use este modo quando o lote for aplicado a uma única empresa.' : 'Selecione este modo para trabalhar com uma única empresa do escopo.'}
                            onClick={() => setActiveMode('single')}
                          />
                          {canUseMultiMode ? (
                            <ModeOptionCard
                              title="Importação multiempresa"
                              description="Use quando o lote precisar distribuir linhas por múltiplas empresas elegíveis do contexto atual."
                              active={activeMode === 'multi'}
                              helper={activeMode === 'multi' ? 'Use este modo quando o arquivo exigir distribuição por CNPJ para múltiplas empresas elegíveis.' : 'Selecione este modo para usar o layout multiempresa dentro do escopo permitido.'}
                              onClick={() => setActiveMode('multi')}
                            />
                          ) : null}
                        </div>
                      ) : null}

                      {isInResendMode ? (
                        <div className="space-y-3">
                          <ResendScopeDisplay
                            channelId={resendContext!.channelId}
                            companyId={resendContext!.companyId}
                            channelName={historyData?.data?.[0]?.channelName || (Array.isArray(scopesData?.data) ? scopesData.data.find((item: any) => item.id === resendContext!.channelId)?.name : undefined)}
                            companyName={historyData?.data?.[0]?.companyName || (Array.isArray(companiesData?.data) ? companiesData.data.find((item: any) => item.id === resendContext!.companyId)?.name : undefined)}
                          />
                          <Button onClick={handleCancelResend} variant="outline" className="w-full md:w-auto">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Cancelar reenvio
                          </Button>
                        </div>
                      ) : (
                          <Card className="border-slate-200">
                          <CardHeader>
                            <CardTitle>{user?.role === 'client_rh' ? (activeMode === 'multi' && scope?.isHierarchicalScope ? 'Empresas elegíveis do seu escopo' : 'Seleção da empresa do envio') : 'Contexto operacional do lote'}</CardTitle>
                            <CardDescription>
                              {user?.role === 'client_rh'
                                ? activeMode === 'multi' && scope?.isHierarchicalScope
                                  ? 'Visualize de forma direta quais empresas do seu escopo podem receber linhas no arquivo multiempresa.'
                                  : scope?.isHierarchicalScope
                                    ? 'Selecione a empresa que deve receber esta importação simples.'
                                    : 'A importação será executada na empresa do seu perfil. Use esta área apenas para confirmar o contexto.'
                                : isGlobalMultiWithoutScope
                                  ? 'Neste modo, não existe escopo fixo prévio. Cada linha da planilha multiempresa será direcionada pelo CNPJ informado.'
                                  : 'Selecione canal e empresa para liberar as etapas seguintes e definir explicitamente o modo de importação.'}
                            </CardDescription>
                          </CardHeader>

                          <CardContent>
                            <ImportScopeSelector onScopeChange={setScope} disabled={!!resendContext} mode={activeMode} />
                          </CardContent>
                        </Card>
                      )}
                    </div>

                    {activeMode === 'multi' && scopeReady ? (
                      <Alert className="border-violet-200 bg-violet-50">
                        <Layers3 className="h-4 w-4 text-violet-700" />
                        <AlertDescription className="text-violet-950">
                          {isGlobalMultiWithoutScope
                            ? 'No modo multiempresa global, o destino de cada linha será resolvido pelo CNPJ informado na planilha.'
                            : isGlobalAdmin
                              ? `No modo multiempresa, o lote pode distribuir linhas por CNPJ dentro do contexto selecionado.`
                              : scope?.isHierarchicalScope
                                ? `No modo multiempresa, o arquivo poderá distribuir linhas entre ${scope.availableCompanyCount || 0} empresas elegíveis do seu escopo.`
                                : 'No modo multiempresa, confirme que os CNPJs do arquivo pertencem ao escopo elegível da empresa-base selecionada.'}
                        </AlertDescription>
                      </Alert>
                    ) : null}
                  </div>

                  <StepNavigation
                    onNext={goToNextStep}
                    nextDisabled={!scopeReady}
                    nextHelper={scopeReady ? (isGlobalMultiWithoutScope ? 'O modo multiempresa global já está pronto. Siga para conferir o layout e preparar o arquivo.' : user?.role === 'client_rh' && scope?.isHierarchicalScope ? 'Com a seleção concluída, siga para baixar o modelo correto e o dicionário de campos.' : 'O contexto já foi definido. Você pode seguir para as orientações e preparação do arquivo.') : 'Defina canal e empresa para liberar a próxima etapa da jornada.'}
                  />
                </StepCard>
              )}

              {activeStep === 2 && (
                <StepCard
                  sectionId="nr1-modelo"
                  step={2}
                  title="Modelo e orientações"
                  description="Baixe primeiro o modelo compatível com o modo selecionado e mantenha o dicionário de campos à vista durante o preenchimento."
                  ready={scopeReady}
                >
                  <div className="space-y-4">
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base">Arquivos principais desta etapa</CardTitle>
                        <CardDescription>
                          Veja somente o material compatível com a escolha feita no passo 1 e mantenha o dicionário de campos por perto durante o preenchimento.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
                          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                            <div className="flex items-start gap-3">
                              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-blue-700 shadow-sm">
                                <FileSpreadsheet className="h-5 w-5" />
                              </div>
                              <div className="space-y-2">
                                <p className="text-sm font-semibold text-blue-950">
                                  {activeMode === 'multi' ? 'Modelo multiempresa' : 'Modelo empresa comum'}
                                </p>
                                <p className="text-sm leading-6 text-blue-900">
                                  {activeMode === 'multi'
                                    ? 'Use este modelo quando o arquivo precisar distribuir linhas entre empresas elegíveis do escopo.'
                                    : 'Use este modelo quando todo o lote for enviado para uma única empresa definida no passo 1.'}
                                </p>
                                <Badge variant="secondary" className="bg-white text-blue-800">
                                  {activeMode === 'multi' ? 'Modelo selecionado para este envio' : 'Modelo selecionado para este envio'}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                            <div className="space-y-2">
                              <p className="text-sm font-semibold text-emerald-950">Dicionário de campos</p>
                              <p className="text-sm leading-6 text-emerald-900">
                                Consulte este material para conferir nomes, formatos esperados e regras de preenchimento antes do envio.
                              </p>
                              <Badge variant="secondary" className="bg-white text-emerald-800">
                                Referência da etapa
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                          {activeMode === 'multi'
                            ? 'No fluxo multiempresa, confirme apenas se os CNPJs e as linhas do arquivo estão preparados para distribuição entre as empresas elegíveis do lote.'
                            : 'No fluxo simples, confirme apenas se o arquivo foi preenchido para a empresa escolhida no passo 1.'}
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-slate-900">Downloads</p>
                            <p className="text-sm leading-6 text-slate-600">
                              Baixe aqui o modelo compatível com este envio e o dicionário de campos antes de avançar.
                            </p>
                          </div>
                          <div className="mt-4 grid gap-3 lg:grid-cols-2">
                            <Button
                              onClick={activeMode === 'multi' ? handleDownloadMultiTemplate : handleDownloadTemplate}
                              disabled={activeMode === 'multi' ? downloadingMultiTemplate || isProcessing : downloadingTemplate || isProcessing}
                              className="w-full justify-between"
                            >
                              <span className="flex items-center gap-2">
                                <Download className="h-4 w-4" />
                                {activeMode === 'multi' ? 'Modelo multiempresa' : 'Modelo empresa comum'}
                              </span>
                              <span>
                                {activeMode === 'multi'
                                  ? downloadingMultiTemplate ? 'Baixando...' : 'Baixar'
                                  : downloadingTemplate ? 'Baixando...' : 'Baixar'}
                              </span>
                            </Button>
                            <Button
                              onClick={handleDownloadDictionary}
                              disabled={downloadingDictionary || isProcessing}
                              variant="outline"
                              className="w-full justify-between"
                            >
                              <span className="flex items-center gap-2">
                                <Download className="h-4 w-4" />
                                Dicionário de campos
                              </span>
                              <span>{downloadingDictionary ? 'Baixando...' : 'Baixar'}</span>
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <StepNavigation
                    onBack={goToPreviousStep}
                    onNext={goToNextStep}
                    nextDisabled={!scopeReady}
                    nextHelper={isGlobalMultiWithoutScope ? 'No multiempresa global, mantenha o layout multiempresa e o arquivo de referência à vista antes do envio.' : 'Quando estiver com o modelo correto e o dicionário de campos em mãos, siga para o envio da planilha.'}
                  />
                </StepCard>
              )}

              {activeStep === 3 && (
                <StepCard
                  sectionId="nr1-upload"
                  step={3}
                  title="Enviar planilha"
                  description="Envie o lote com clareza, mantenha o checklist à vista e preserve o mesmo comportamento técnico da validação atual."
                  ready={!!selectedFile || isProcessing || reviewReady}
                >
                  <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
                    <div className="space-y-4">
                      <ImportUploadZone
                        onFileSelected={handleFileSelect}
                        onUpload={handleUpload}
                        isLoading={isProcessing}
                        disabled={!scopeReady}
                      />

                      <Card className="border-slate-200 bg-slate-50/80">
                        <CardHeader>
                          <CardTitle className="text-base">Arquivo em foco</CardTitle>
                          <CardDescription>O arquivo selecionado continua passando exatamente pela mesma validação do fluxo atual.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm text-slate-700">
                          <div className="rounded-xl border border-slate-200 bg-white p-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Arquivo</p>
                            <p className="mt-1 font-semibold text-slate-900">{selectedFile ? selectedFile.name : 'Nenhum arquivo selecionado no momento'}</p>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-white p-3">
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Contexto liberado</p>
                            <p className="mt-1 font-semibold text-slate-900">{scopeReady ? 'Sim, pronto para validar' : 'Ainda não. Complete o passo 1 para liberar o envio.'}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Card className="border-slate-200 bg-slate-50/80">
                      <CardHeader>
                        <CardTitle className="text-base">Checklist antes de validar</CardTitle>
                        <CardDescription>O usuário precisa saber exatamente o que será conferido ao enviar o arquivo.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3 text-sm text-slate-700">
                        <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                          <p>Confirme se a planilha foi preenchida usando o modelo compatível com o contexto atual.</p>
                        </div>
                        <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3">
                          <ClipboardCheck className="mt-0.5 h-4 w-4 text-blue-600" />
                          <p>Revise contratos e integrações antes de enviar o arquivo para evitar retrabalho na validação.</p>
                        </div>
                        <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3">
                          <Layers3 className="mt-0.5 h-4 w-4 text-violet-600" />
                          <p>
                            {activeMode === 'multi'
                              ? 'No fluxo multiempresa, confirme que o lote está preparado para distribuição por empresas do mesmo grupo elegível.'
                              : 'No fluxo de empresa comum, confirme que todo o lote pertence à empresa definida no passo 1.'}
                          </p>
                        </div>
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm leading-6 text-blue-950">
                          A camada de UX foi reorganizada para orientar melhor o envio, mas o processamento, a leitura do arquivo e a validação continuam exatamente os mesmos do fluxo original.
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <StepNavigation
                    onBack={goToPreviousStep}
                    onNext={goToNextStep}
                    backDisabled={isProcessing}
                    nextDisabled={!reviewReady}
                    nextHelper={reviewReady ? 'A validação já concluiu a análise do lote. Você pode seguir para a revisão final.' : 'Envie e valide a planilha para liberar automaticamente a etapa de revisão e conclusão.'}
                  />
                </StepCard>
              )}

              {activeStep === 4 && (
                <StepCard
                  sectionId="nr1-revisao"
                  step={4}
                  title="Revisão e conclusão"
                  description="Concentre nesta etapa a leitura final do lote, dos alertas e das ações para concluir ou preparar um reenvio com segurança."
                  ready={reviewReady}
                >
                  {displayResult && !isProcessing ? (
                    <div className="space-y-4">
                      <div className="grid gap-3 md:grid-cols-3">
                        <StatusChip label="Linhas totais" value={String(displayResult.totalRows)} positive={displayResult.totalRows > 0} />
                        <StatusChip label="Linhas válidas" value={String(displayResult.validRowsCount)} positive={displayResult.validRowsCount > 0} />
                        <StatusChip
                          label="Linhas com ajustes"
                          value={String(displayResult.invalidRowsCount)}
                          positive={displayResult.invalidRowsCount === 0}
                        />
                      </div>

                      <ImportResultPanel
                        totalRows={displayResult.totalRows}
                        validRowsCount={displayResult.validRowsCount}
                        invalidRowsCount={displayResult.invalidRowsCount}
                        errors={displayResult.errors || []}
                        importedPreview={displayResult.importedPreview || []}
                        importId={displayResult.importId}
                        importHistoryId={displayResult.importHistoryId || importResult?.importHistoryId}
                        idempotencyReport={displayResult.idempotencyReport?.details || displayResult.idempotencyReport}
                        onConfirm={handleConfirmImportWithCleanup}
                        onDiscard={handleDiscardImport}
                        isConfirming={false}
                        onConfirmationSuccess={() => {
                          historyQuery.refetch();
                        }}
                      />

                      {(displayResult.invalidRowsCount > 0 || (displayResult as any).invalidCount > 0) && (
                        <Card className="border-orange-200 bg-orange-50">
                          <CardHeader>
                            <CardTitle className="text-orange-950">Corrigir e reenviar o lote</CardTitle>
                            <CardDescription className="text-orange-900">
                              O sistema encontrou pendências na validação. Corrija a planilha e volte a enviá-la mantendo o mesmo contexto operacional.
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <Button
                              onClick={() => {
                                setSelectedFile(null);
                                setImportResult(null);
                                setError(null);
                                setIsProcessing(false);
                                setResendContext({
                                  importHistoryId: displayResult.importHistoryId || importResult?.importHistoryId || 0,
                                  channelId: scope?.channelId || 0,
                                  companyId: scope?.companyId || 0,
                                  fileName: '',
                                });
                                setActiveStep(3);
                              }}
                              className="bg-orange-600 hover:bg-orange-700"
                            >
                              Reenviar planilha corrigida
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  ) : (
                    <Card className="border-dashed border-slate-300 bg-slate-50">
                      <CardContent className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                        <ShieldCheck className="h-9 w-9 text-slate-400" />
                        <div className="space-y-1">
                          <p className="font-semibold text-slate-900">A revisão do lote aparece aqui</p>
                          <p className="max-w-2xl text-sm leading-6 text-slate-600">
                            Depois que a planilha for validada no passo 3, esta etapa exibirá a prévia das linhas, os erros encontrados, os registros já existentes e as ações finais de confirmação ou descarte.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <StepNavigation
                    onBack={goToPreviousStep}
                    backDisabled={isProcessing}
                    nextHelper="A revisão final concentra o resultado validado do lote sem alterar confirmações, descarte, histórico ou integrações já existentes."
                  />
                </StepCard>
              )}

              <Card id="nr1-historico" className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-1">
                      <CardTitle>Histórico</CardTitle>
                      <CardDescription>
                        Consulte esta área apenas quando precisar revisar um lote anterior ou realizar reenvio.
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => historyQuery.refetch()} disabled={!historyEnabled || historyQuery.isFetching}>
                        <RefreshCw className={`mr-2 h-4 w-4 ${historyQuery.isFetching ? 'animate-spin' : ''}`} />
                        Atualizar histórico
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setHistoryExpanded((prev) => !prev)}>
                        {historyExpanded ? 'Ocultar histórico' : 'Abrir histórico'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!historyExpanded ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                      Use o histórico apenas quando precisar consultar um lote anterior ou iniciar um reenvio.
                    </div>
                  ) : !historyEnabled && !selectedHistoryId ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600">
                      Selecione primeiro o contexto operacional da importação para liberar o histórico e a rastreabilidade deste fluxo.
                    </div>
                  ) : selectedHistoryId ? (
                    <div className="space-y-4">
                      <ImportStatusLegend />
                      <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={() => setSelectedHistoryId(null)}>
                          <ArrowLeft className="mr-2 h-4 w-4" />
                          Voltar para a lista
                        </Button>
                      </div>
                      <ImportHistoryDetails
                        importId={selectedHistoryId}
                        onClose={() => setSelectedHistoryId(null)}
                        onReenviar={handleReenviar}
                      />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <ImportStatusLegend />
                      <ImportHistoryList
                        channelId={scope?.channelId}
                        companyId={scope?.companyId}
                        onVerDetalhes={handleVerDetalhes}
                        onReenviar={handleReenviar}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <aside className="space-y-4 xl:sticky xl:top-24 xl:self-start">
              {activeStep !== 2 && (
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-base">Downloads</CardTitle>
                    <CardDescription>Baixe aqui os modelos e o dicionário de campos sempre que precisar.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      onClick={handleDownloadTemplate}
                      disabled={downloadingTemplate || isProcessing}
                      variant={activeMode === 'single' ? 'default' : 'outline'}
                      className="w-full justify-between"
                    >
                      <span className="flex items-center gap-2">
                        <Download className="h-4 w-4" />
                        Modelo empresa comum
                      </span>
                      <span>{downloadingTemplate ? 'Baixando...' : 'Baixar'}</span>
                    </Button>
                    {canUseMultiMode ? (
                      <Button
                        onClick={handleDownloadMultiTemplate}
                        disabled={downloadingMultiTemplate || isProcessing}
                        variant={activeMode === 'multi' ? 'default' : 'outline'}
                        className="w-full justify-between"
                      >
                        <span className="flex items-center gap-2">
                          <Download className="h-4 w-4" />
                          Modelo multiempresa
                        </span>
                        <span>{downloadingMultiTemplate ? 'Baixando...' : 'Baixar'}</span>
                      </Button>
                    ) : null}
                    <Button
                      onClick={handleDownloadDictionary}
                      disabled={downloadingDictionary || isProcessing}
                      variant="outline"
                      className="w-full justify-between"
                    >
                      <span className="flex items-center gap-2">
                        <Download className="h-4 w-4" />
                        Dicionário de campos
                      </span>
                      <span>{downloadingDictionary ? 'Baixando...' : 'Baixar'}</span>
                    </Button>
                  </CardContent>
                </Card>
              )}

            </aside>
          </div>
        </div>
      </div>
    </SalviaLayout>
  );
}
